import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Edit2, Trash2, X, Shield, User } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "super_admin", label: "Super Admin", desc: "Full access + manage users" },
  { value: "admin", label: "Admin", desc: "Full content + leads access" },
  { value: "counselor", label: "Counselor", desc: "Leads view/update only" },
  { value: "editor", label: "Editor", desc: "Content edit only, no leads" },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Not authorized — super admin only"));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ email: "", password: "", role: "counselor", name: "", active: true });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) {
        const payload = { ...editing };
        if (!payload.password) delete payload.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
      } else {
        await api.post("/admin/users", editing);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-3xl" data-testid="admin-users-title">Users & Roles</h1>
          <p className="text-neutral-500 mt-1">Only super admins can add or remove users</p>
        </div>
        <button onClick={openNew} className="btn-primary-eq text-sm" data-testid="users-add"><Plus className="w-4 h-4" /> Add User</button>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold flex items-center gap-2">
                  {u.role === "super_admin" ? <Shield className="w-4 h-4 text-sky-500" /> : <User className="w-4 h-4 text-neutral-400" />}
                  {u.name || "-"}
                </td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3"><span className="px-2 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold">{u.role}</span></td>
                <td className="px-4 py-3">{u.active !== false ? "Yes" : "No"}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => setEditing({ ...u, password: "" })} className="p-1.5 hover:bg-neutral-100 rounded" data-testid={`user-edit-${u.id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(u.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="text-center py-16 text-neutral-500">No users. Add your first counselor or editor.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="user-form">
            <div className="flex justify-between mb-4">
              <h3 className="font-display font-bold text-xl">{editing.id ? "Edit" : "New"} User</h3>
              <button type="button" onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input required placeholder="Name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                     className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid="user-name" />
              <input required type="email" placeholder="Email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                     className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid="user-email" />
              <input type="password" placeholder={editing.id ? "Password (leave blank to keep)" : "Password"} value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                     className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid="user-password" required={!editing.id} />
              <div>
                <label className="text-xs font-semibold text-neutral-600">Role</label>
                <div className="grid gap-2 mt-1">
                  {ROLES.map((r) => (
                    <label key={r.value} className={`p-3 rounded-lg border cursor-pointer ${editing.role === r.value ? "border-sky-500 bg-sky-50" : "border-neutral-200"}`}>
                      <input type="radio" name="role" checked={editing.role === r.value} onChange={() => setEditing({ ...editing, role: r.value })} className="hidden" />
                      <div className="font-semibold text-sm">{r.label}</div>
                      <div className="text-xs text-neutral-500">{r.desc}</div>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> <span className="text-sm">Active</span></label>
            </div>
            <button type="submit" className="btn-primary-eq w-full justify-center mt-6" data-testid="user-save">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
